/**
 * Resolve a slot's "anatomic up" sign in list-index space, preferring the real
 * per-slice ImagePositionPatient Cornerstone holds for the viewport's first and
 * last imageIds over the metadata.json sidecar.
 *
 * Why prefer Cornerstone over studyMetadata.image_position_patient (mirrors the
 * jump/write path fixed in PR #544's sliceWorldPositionFromMetadata):
 *   - The canonical sidecar usually omits image_position_patient (it carries
 *     only a scalar, sometimes sign-flipped z_position), so the sidecar-only
 *     path yields sign 0 and the scroll direction falls back to the caller's
 *     default — which is backwards on the plane-reversed coronal/sagittal
 *     reformats the canonical pipeline emits.
 *   - Cornerstone's imagePlaneModule for a *streamed* slice is the real DICOM
 *     header IPP (wadouri wins over the synthesized z_position*normal eager
 *     cache once the frame streams — see the provider-order note in
 *     dicomLoader.ts), and it's keyed by the imageId, so passing the viewport's
 *     imageIds list (imageIds[0] / imageIds[N-1] = list-index 0 / N-1) yields
 *     the sign directly in list-index space regardless of K-step reorder.
 *
 * The sidecar first/last IPP is a last-resort fallback for the window before
 * Cornerstone has cached the slice geometry; recompute once the volume has
 * streamed so the real IPP supersedes the synthesized eager-cache value.
 */

import { computeListAnatomicSign, type Vec3 } from "@/utils/anatomicScroll";
import type { Plane } from "@/utils/seriesClassification";
import {
  sliceWorldPositionFromMetadata,
  type CornerstoneMetaDataLike,
} from "@/components/volumeViewport.sliceWorldPosition";

export function resolveListAnatomicSign(
  metaData: CornerstoneMetaDataLike | null,
  imageIds: readonly string[] | null | undefined,
  sidecarFirst: Vec3 | null | undefined,
  sidecarLast: Vec3 | null | undefined,
  plane: Plane,
): -1 | 0 | 1 {
  const csFirst = metaData ? sliceWorldPositionFromMetadata(metaData, imageIds?.[0]) : null;
  const csLast =
    metaData && imageIds && imageIds.length > 0
      ? sliceWorldPositionFromMetadata(metaData, imageIds[imageIds.length - 1])
      : null;
  const first = csFirst ?? sidecarFirst ?? null;
  const last = csLast ?? sidecarLast ?? null;
  return computeListAnatomicSign(first, last, plane);
}
